import { authFetch, BASE_BACKEND_URL } from '../../../auth'
import React, { useCallback, useEffect, useRef } from 'react'
import AsyncSelect from 'react-select/async'
import Select from 'react-select'
import { Box, MenuItem, TextField } from '@mui/material'
import { TASK_QUALIFIER_FIELDS, validateAtLeastOneTaskQualifier } from './TaskAttributeHelper'
import { isOneTimeRecurrence } from '../utils/posTaskSchedule'
import OneTimeMandatoryAlert from './OneTimeMandatoryAlert'
import TaskOptionalSettingCard from './TaskOptionalSettingCard'

const CLIENT_SEARCH_DEBOUNCE_MS = 1000

function TaskClientOrAttribute({
  clientOrAttribute,
  handleClientOrAttributeChange,
  clients,
  handleClientOption,
  getClient,
  abortClientFetchs,
  selectedQualifiers,
  qualifierOptions,
  onQualifierChange,
  validateAtLeastOneQualifier,
  description,
  isClientOrSomeQualifierSelected,
  userFilterChecked,
  setUserFilterChecked,
  users,
  userOptions,
  handleUsersChange,
  isMandatoryTask,
  setIsMandatoryTask,
  recurrenceType,
  onMandatoryBlocked,
  showMandatoryConflict,
  onDismissMandatoryConflict
}) {
  const safeClientOrAttribute = clientOrAttribute ?? ''
  const safeClients = clients ?? []
  const safeSelectedQualifiers = selectedQualifiers ?? {}
  const safeQualifierOptions = qualifierOptions ?? {}
  const safeUsers = users ?? []
  const safeUserOptions = userOptions ?? []
  const pendingClientSearchRef = useRef(null)
  const isClientSearchInFlightRef = useRef(false)
  const clientSearchRequestIdRef = useRef(0)
  const pendingUserSearchRef = useRef(null)
  const userSearchAbortRef = useRef(null)
  const userSearchRequestIdRef = useRef(0)

  const cancelPendingClientSearch = useCallback(() => {
    const pendingSearch = pendingClientSearchRef.current

    if (!pendingSearch) return

    clearTimeout(pendingSearch.timeoutId)
    pendingSearch.resolve([])
    pendingClientSearchRef.current = null
  }, [])

  const loadClientOptions = useCallback(
    (inputValue) => {
      cancelPendingClientSearch()
      const requestId = clientSearchRequestIdRef.current + 1
      clientSearchRequestIdRef.current = requestId

      if (isClientSearchInFlightRef.current) {
        abortClientFetchs()
      }

      if (inputValue.length < 3) return Promise.resolve([])

      return new Promise((resolve) => {
        const timeoutId = setTimeout(async () => {
          pendingClientSearchRef.current = null
          isClientSearchInFlightRef.current = true

          try {
            resolve((await getClient(inputValue)) ?? [])
          } catch {
            // A canceled request should not leave the select in a loading state.
            resolve([])
          } finally {
            if (clientSearchRequestIdRef.current === requestId) {
              isClientSearchInFlightRef.current = false
            }
          }
        }, CLIENT_SEARCH_DEBOUNCE_MS)

        pendingClientSearchRef.current = { timeoutId, resolve }
      })
    },
    [abortClientFetchs, cancelPendingClientSearch, getClient]
  )

  const handleClientBlur = () => {
    cancelPendingClientSearch()
    clientSearchRequestIdRef.current += 1
    isClientSearchInFlightRef.current = false
    abortClientFetchs()
  }

  const cancelPendingUserSearch = useCallback(() => {
    const pendingSearch = pendingUserSearchRef.current

    if (!pendingSearch) return

    clearTimeout(pendingSearch.timeoutId)
    pendingSearch.resolve([])
    pendingUserSearchRef.current = null
    userSearchAbortRef.current?.abort()
  }, [])

  const loadUserOptions = useCallback(
    (inputValue) => {
      cancelPendingUserSearch()
      const query = inputValue.trim()

      if (query.length < 3) {
        const needle = query.toLowerCase()
        const localOptions = needle
          ? safeUserOptions.filter((option) => option.label.toLowerCase().includes(needle))
          : safeUserOptions
        return Promise.resolve(localOptions)
      }

      const requestId = userSearchRequestIdRef.current + 1
      userSearchRequestIdRef.current = requestId

      return new Promise((resolve) => {
        const timeoutId = setTimeout(async () => {
          pendingUserSearchRef.current = null
          const controller = new AbortController()
          userSearchAbortRef.current = controller

          try {
            const response = await authFetch(
              `${BASE_BACKEND_URL}/ubiqua_app_users?q=${encodeURIComponent(query)}&active=true&page=1&per_page=25`,
              {
                method: 'GET',
                signal: controller.signal,
                headers: {
                  Accept: 'application/json',
                  'Content-Type': 'application/json'
                }
              }
            )

            if (!response || userSearchRequestIdRef.current !== requestId) {
              resolve([])
              return
            }

            const data = await response.json()
            if (userSearchRequestIdRef.current !== requestId) {
              resolve([])
              return
            }

            resolve(
              (data?.table || []).map((user) => ({
                value: user.full_name,
                label: `${user.full_name} - ${user.uid}`,
                user
              }))
            )
          } catch {
            resolve([])
          }
        }, CLIENT_SEARCH_DEBOUNCE_MS)

        pendingUserSearchRef.current = { timeoutId, resolve }
      })
    },
    [cancelPendingUserSearch, safeUserOptions]
  )

  useEffect(() => cancelPendingClientSearch, [cancelPendingClientSearch])
  useEffect(() => cancelPendingUserSearch, [cancelPendingUserSearch])

  const noAvailableQualifiers = () =>
    TASK_QUALIFIER_FIELDS.every(({ key }) => (safeQualifierOptions[key] || []).length === 0)

  const isUserFilterChecked = (event) => {
    setUserFilterChecked(event.target.checked)
  }

  const handleMandatoryTaskChange = (event) => {
    if (event.target.checked && isOneTimeRecurrence(recurrenceType)) {
      setIsMandatoryTask(false)
      onMandatoryBlocked?.()
      return
    }

    setIsMandatoryTask(event.target.checked)
  }

  const qualifiersValidationValue =
    (validateAtLeastOneQualifier
      ? validateAtLeastOneQualifier()
      : validateAtLeastOneTaskQualifier(safeSelectedQualifiers)) ?? ''
  const canConfigureOptionalSettings =
    isClientOrSomeQualifierSelected() && safeClientOrAttribute.length > 0

  return (
    <>
      <div>
        <div className="title-promos-form">
          <b className="bold-step">PASO 3: Escoja a qué clientes se debe aplicar esta tarea</b>
        </div>
        <hr className="hr-task-form" />
        <p>Por cliente individual o Atributo(s)</p>

        <div>
          <div className="task-form-control-width">
            <TextField
              select
              fullWidth
              size="small"
              label="Tipo de aplicación"
              value={safeClientOrAttribute}
              onChange={(e) => {
                if (description === '') return
                handleClientOrAttributeChange(e)
              }}
              required
              sx={{
                pointerEvents: description === '' ? 'none' : 'auto',
                opacity: description === '' ? 0.6 : 1,
                backgroundColor: description === '' ? '#f0f0f0' : 'transparent',
                '& .MuiInputBase-input': {
                  cursor: description === '' ? 'not-allowed' : 'pointer'
                }
              }}
            >
              <MenuItem value="">Escoja una opción</MenuItem>
              <MenuItem value="client">Cliente individual</MenuItem>
              {!noAvailableQualifiers() && <MenuItem value="qualifier">Atributo(s)</MenuItem>}
            </TextField>
          </div>
        </div>
      </div>

      {safeClientOrAttribute === 'client' && (
        <div className="task-form-section">
          <div className="title-promos-form">
            <p>
              <b>PASO 3.1: Escoja los clientes que aplican para esta tarea</b>
            </p>
          </div>
          <div className="fake-validator-container">
            <input
              className="input-fake-validator"
              type="text"
              required
              value={safeClients.map((client) => client.label).join(', ')}
              readOnly
            />
            <AsyncSelect
              className="clients"
              placeholder="Buscar cliente por nombre o código"
              noOptionsMessage={() => 'No hay resultados'}
              loadingMessage={() => 'Cargando clientes...'}
              name="selectOptionClients"
              loadOptions={loadClientOptions}
              isClearable={true}
              searchParamName="q"
              isSearchable={true}
              isMulti
              value={safeClients}
              onChange={handleClientOption}
              onBlur={handleClientBlur}
            />
          </div>
        </div>
      )}

      {safeClientOrAttribute === 'qualifier' && (
        <div className="task-form-section">
          <div className="title-promos-form">
            <b>
              PASO 3.1: Escoja los atributos que deben de tener los clientes para activar esta
              tarea. Mínimo debe de escoger 1 atributo
            </b>
          </div>

          {TASK_QUALIFIER_FIELDS.map(({ key, label }, index) => {
            const options = safeQualifierOptions[key] || []
            const selected = safeSelectedQualifiers[key] || []
            const zIndex = TASK_QUALIFIER_FIELDS.length - index

            return (
              <React.Fragment key={key}>
                <div className="qualifier-title">{label}</div>
                <div className="fake-validator-container" style={{ zIndex }}>
                  {options.length > 0 && (
                    <input
                      className="input-fake-validator"
                      type="text"
                      required
                      value={qualifiersValidationValue}
                      readOnly
                    />
                  )}
                  <Select
                    placeholder="Buscar o escoger una opcion"
                    noOptionsMessage={() => 'Buscar'}
                    loadingMessage={() => 'Cargando...'}
                    className="qualifier"
                    isDisabled={options.length === 0}
                    isClearable={true}
                    isMulti
                    onChange={(event) => onQualifierChange(key, event)}
                    options={options}
                    value={selected.map((q) => ({ value: q, label: q }))}
                  />
                </div>
              </React.Fragment>
            )
          })}
        </div>
      )}

      {canConfigureOptionalSettings && (
        <Box className="task-optional-settings">
          <TaskOptionalSettingCard
            badge="OPCIONAL 3.2"
            question="¿Desea escoger usuarios específicos para esta tarea?"
            description="Active esta opción si desea asignar manualmente uno o más usuarios específicos para esta tarea. La tarea será visible únicamente para los usuarios asignados."
            checkboxLabel="Sí, escoger usuarios"
            checked={Boolean(userFilterChecked)}
            onChange={isUserFilterChecked}
          >
            {Boolean(userFilterChecked) && (
              <div className="fake-validator-container">
                {safeUserOptions.length > 0 && (
                  <input
                    className="input-fake-validator"
                    type="text"
                    required
                    placeholder="Buscar usuario por nombre o código"
                    value={safeUsers.map((user) => user.label).join(', ')}
                    readOnly
                  />
                )}
                <AsyncSelect
                  className="qualifier"
                  placeholder="Buscar usuario por nombre o código"
                  noOptionsMessage={({ inputValue }) =>
                    inputValue.trim().length < 3
                      ? 'Escriba al menos 3 caracteres'
                      : 'No hay resultados'
                  }
                  loadingMessage={() => 'Cargando usuarios...'}
                  loadOptions={loadUserOptions}
                  defaultOptions={safeUserOptions}
                  isClearable={true}
                  isSearchable={true}
                  isMulti
                  value={safeUsers}
                  onChange={handleUsersChange}
                />
              </div>
            )}
          </TaskOptionalSettingCard>

          <TaskOptionalSettingCard
            badge="OPCIONAL 3.3"
            question="¿Desea que sea obligatoria terminar esta tarea antes de comenzar otra visita?"
            description="Active esta opción si desea que el usuario no pueda hacer Check In/OUT sin terminar esta actividad primero."
            checkboxLabel="Sí, hacerla obligatoria"
            checked={Boolean(isMandatoryTask)}
            onChange={handleMandatoryTaskChange}
          />

          <OneTimeMandatoryAlert
            open={Boolean(showMandatoryConflict)}
            onClose={onDismissMandatoryConflict}
          />
        </Box>
      )}
    </>
  )
}

export default TaskClientOrAttribute
